import React from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { imageUrl } from 'js/utils/image_url';
import { useDragClick } from 'js/social/shared/MarketingCarousel';

const Container = styled.div`
  @import 'stylesheets/design_system/_deprecated_grid.scss';
  background-color: #ffffff;
  border: 1px solid #dddde2;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: opacity 0.2s ease;
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    opacity: 0.95;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 24px 24px 48px 24px;
  flex: 1;
  min-height: 0;
`;

const InnerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  flex: 1;
  justify-content: space-between;
`;

const BackgroundSection = styled.div`
  background-color: #b9d5d7;
  width: 100%;
  border-radius: 8px;
  position: relative;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 164px;
`;

const PodcastCard = styled.div`
  background: #ffffff;
  border: 0.462px solid #dddde2;
  border-radius: 7.397px;
  width: 162px;
  padding: 6.934px 7.397px;
  display: flex;
  flex-direction: column;
  gap: 7.397px;
  position: relative;
  z-index: 1;
`;

const PodcastCardContent = styled.div`
  display: flex;
  gap: 7.397px;
`;

const PodcastThumbnail = styled.div`
  background: #f5f5f5;
  border: 0.407px solid #dddde2;
  border-radius: 3.258px;
  height: 68.42px;
  width: 48.464px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PodcastInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.698px;
  flex: 1;
  min-width: 0;
`;

const PodcastTitle = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 7.397px;
  font-weight: 600;
  line-height: 9.246px;
  color: #141413;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PodcastAuthors = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 6.472px;
  font-weight: 400;
  line-height: 9.246px;
  color: #535366;
  margin: 0;
`;

const AudioPlayer = styled.div`
  background: #f1f3f4;
  border-radius: 3.698px;
  padding: 3px 6.472px;
  display: flex;
  align-items: center;
  gap: 5.548px;
  height: 24.039px;
`;

const PlayIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TimeText = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 6.472px;
  font-weight: 400;
  color: #000000;
  margin: 0;
  white-space: nowrap;
  flex-shrink: 0;
`;

const Timeline = styled.div`
  flex: 1;
  height: 1.849px;
  background: #d9d9d9;
  border-radius: 92.459px;
  min-width: 0;
`;

const VolumeIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Badge = styled.div`
  position: absolute;
  background: #edf4f4;
  border: 1px solid #d4e6e6;
  border-radius: 8px;
  padding: 0 8px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BadgeText = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #164c50;
  margin: 0;
  white-space: nowrap;
`;

const Badge1 = styled(Badge)`
  left: 15%;
  top: 25%;
`;

const Badge2 = styled(Badge)`
  left: 20%;
  bottom: 25%;
`;

const Badge3 = styled(Badge)`
  right: 10%;
  top: 20%;
`;

const Badge4 = styled(Badge)`
  right: 15%;
  bottom: 30%;
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
`;

const Heading = styled.h2`
  font-family: 'DM Sans', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 32px;
  color: #141413;
  text-align: center;
  margin: 0;
  width: 100%;
`;

const Subheading = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #141413;
  text-align: center;
  margin: 0;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`;

/**
 * Marketing card promoting the podcast library.
 * Links to the podcast library page and tracks impressions.
 *
 * This component is intended to be used within the MarketingCarousel
 * on the home feed.
 *
 * @example
 * ```tsx
 * <PodcastLibraryCarouselAd />
 * ```
 */
export const PodcastLibraryCarouselAd: React.FC = () => {
  const { handleMouseDown, handleMouseMove, handleMouseUp, handleClick } =
    useDragClick(() => {
      window.location.href = routes.podcast_library_url();
    });

  return (
    <ImpressionWatcher
      source="podcast_lib.home.carousel"
      entity_type={ImpressionEntity.UnMapped}
      entity_id={0}
    >
      <Wrapper>
        <Container
          onClick={handleClick}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <ContentWrapper>
            <InnerContent>
              <BackgroundSection>
                <PodcastCard>
                  <PodcastCardContent>
                    <PodcastThumbnail>
                      <ThumbnailImage
                        src={imageUrl('scribd/document_thumbnail_fallback.svg')}
                        alt=""
                      />
                    </PodcastThumbnail>
                    <PodcastInfo>
                      <PodcastTitle>
                        Open Access Meets Discoverability: Citations to Articles
                        Posted to Academia.edu
                      </PodcastTitle>
                      <PodcastAuthors>
                        By Yuri Niyazov, Carl Vogel, Richard Price, Ben Lund,
                        Da... view more
                      </PodcastAuthors>
                    </PodcastInfo>
                  </PodcastCardContent>
                  <AudioPlayer>
                    <PlayIconWrapper>
                      <Icon name="play_arrow" size={10} />
                    </PlayIconWrapper>
                    <TimeText>0:00/ 4:45</TimeText>
                    <Timeline />
                    <VolumeIconWrapper>
                      <Icon name="volume_up" size={10} />
                    </VolumeIconWrapper>
                  </AudioPlayer>
                </PodcastCard>
                <Badge1>
                  <BadgeText>Mathematics</BadgeText>
                </Badge1>
                <Badge2>
                  <BadgeText>Algebra</BadgeText>
                </Badge2>
                <Badge3>
                  <BadgeText>Abelian groups</BadgeText>
                </Badge3>
                <Badge4>
                  <BadgeText>Economics</BadgeText>
                </Badge4>
              </BackgroundSection>
              <TextContent>
                <Heading>Discover 35K+ podcasts</Heading>
                <Subheading>
                  Get research insights in minutes, not hours
                </Subheading>
                <ButtonWrapper>
                  <Button icon="mic">Explore podcasts</Button>
                </ButtonWrapper>
              </TextContent>
            </InnerContent>
          </ContentWrapper>
        </Container>
      </Wrapper>
    </ImpressionWatcher>
  );
};
